/**
 * @description Component for using dropdown
 * @param {boolean} showDropdown - Flag to toggle the dropdown state
 * @param {string | React.ReactNode} dropdownLabel -  Question/Label shown when no value is selected on the button
 * @param {string | number} dropdownValue - Value of the selected item shown on the button
 * @param {React.ReactNode} dropdownIcon - What should be the icon of the dropdown
 * @param {string[] | number[] | React.ReactNode[]} list - Items to be shown when dropdown is open
 * @param {boolean} disabled - Flag to disbale the dropdown button
 * @param {string} errorMessage - To display the error message
 * @param {function} onBlur - Method to handle when onBlur event happens
 * @param {function} dropdownButtonOnClick - Method to handle when button is clicked
 * @param {string} dropdownClassname - Styings to be applied to dropdown
 * @param {string} errorMessageClassname - Styings to be applied to error body
 * @param {string} dropdownBodyClassname - Styings to be applied to dropdown body.
 * @param {string} width - Width of the dropdown
 * @param {string} height - Height of the dropdown
 * @param {string} id - ID passed to the dropdown
 * @param {string[]} listId - ID passed to the list items
 * @param {React.ReactNode} children - To display childrens of Dropdown element
 * @param {function} listItemOnClick - Methiod to handle lost items click event
 * @param {LegacyRef<HTMLDivElement>} dropdownRef - Ref to access the dropdown
 * @returns {React.FunctionComponent<IDropdown.IProps>} - Returns JSX for dropdown
 */
import React from "react";
import clsx from "clsx";

import { IDropdown } from "./Dropdown";
import { DropdownIcon } from "../../../assets/Icons";
import ErrorField from "../ErrorField";

const Dropdown: React.FunctionComponent<IDropdown.IProps> = ({
  showDropdown,
  dropdownLabel,
  dropdownValue,
  dropdownIcon = <DropdownIcon fillColor="stroke-black" />,
  list = [],
  disabled,
  errorMessage,
  dropdownClassname = "bg-white",
  errorMessageClassname,
  dropdownBodyClassname,
  width = "w-96",
  height,
  id,
  onBlur,
  listId = [],
  children,
  listItemOnClick,
  dropdownButtonOnClick,
  dropdownRef,
  countryList,
  getId,
  dropDownValueClassNameApply,
}) => {
  const getRandomId = () => {
    const id = window.crypto.getRandomValues(new Uint32Array(1))[0];
    return id;
  };

  return (
    <div className="w-full " ref={dropdownRef}>
      <button
        onClick={dropdownButtonOnClick}
        onBlur={onBlur}
        id={id}
        className={clsx(
          "focus:outline-none w-full inline-flex text-left flex-col relative",
          showDropdown ? "rounded-t" : "rounded",
          disabled ? "cursor-not-allowed" : "",
          height,
          !countryList && width,
          dropdownClassname
        )}
        type="button"
        disabled={disabled}
      >
        <div
          className={clsx(
            !countryList && "px-4 py-3",
            "flex items-center w-full sm:w-full mx-auto sm:mx-0 justify-between space-x-2"
          )}
        >
          <div
            className={`w-full flex justify-between items-center ${
              !dropDownValueClassNameApply && "border-b"
            }`}
          >
            <div className={dropdownValue ? "flex justify-start" : "flex justify-start"}>
              {dropdownValue ? dropdownValue : dropdownLabel}
            </div>
            <div>{dropdownIcon}</div>
          </div>
        </div>
        <div
          className={clsx(
            "z-20 bg-white divide-y divide-gray-100 rounded-b absolute top-[55px] left-0 w-full h-48 overflow-auto",
            !showDropdown && "hidden",
            dropdownBodyClassname,
            !countryList && width
          )}
        >
          {children ? (
            children
          ) : countryList ? (
            <ul className="py-1 text-sm text-gray-700 dark:text-gray-700">
              {countryList.map((el, id) => (
                <li
                  onClick={listItemOnClick}
                  className="block px-4 py-3 hover:bg-gray-100"
                  key={id + getRandomId()}
                  id={listId[id]}
                >
                  <span className="flex items-center justify-start space-x-2 text-base font-medium text-black font-ssp">
                    <img src={`/images/png/${el.icon}`} alt="" width="35px" height="35px" />
                    <div>{el.name}</div>
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <ul className="py-1 text-sm text-gray-700 dark:text-gray-700">
              {list.map((el: any, id) => (
                <li
                  onClick={(e) => {
                    if (listItemOnClick) listItemOnClick(e);
                    if (getId) getId(id);
                  }}
                  className="block px-4 py-3 hover:bg-gray-100"
                  key={id + getRandomId()}
                  id={listId[id]}
                >
                  {el?.vendorCode ?? el}
                </li>
              ))}
            </ul>
          )}
        </div>
      </button>
      {errorMessage && (
        <ErrorField errorClassname={errorMessageClassname} errorMessage={errorMessage} />
      )}
    </div>
  );
};

export default Dropdown;
